import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { Affix, Button, Tooltip } from "antd";
import { ColumnProps, TablePaginationConfig } from "antd/lib/table";
import { nanoid } from "nanoid";
import { useEffect, useState } from "react";
import { FilterDrawer } from "./FilterDrawer";
import { ADMIN_getFilteredAdminLogs } from "General/APIQueryParams";
import { useTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { Table, TableProps } from "Components/General/Table";
import { SorterResult } from "antd/es/table/interface";
import { dayjs } from "Config/dayjs";

const getDefault48HoursFilter = (): ADMIN_getFilteredAdminLogs => ({
  startTime: +dayjs().subtract(48, "hours").format("x"),
  endTime: +dayjs().format("x"),
});

interface AdminLogEntryWithUid extends API_AdminLogEntry {
  uid: string;
}

const sortByMap: { [key in keyof API_AdminLogEntry]?: ADMIN_getFilteredAdminLogs["sortBy"] } = {
  actor: "ACTOR",
  ip: "IP",
  logType: "LOGTYPE",
  logTime: "TIME",
};

export const AdminLogs = () => {
  const { t } = useTranslation(I18N_NS.ADMIN);
  const [logTypes, setLogTypes] = useState<string[]>([]);
  const [tablePagination, setTablePagination] = useState<TablePaginationConfig>({
    hideOnSinglePage: true,
    current: 1,
    pageSize: 50,
  });
  const [tableSorter, setTableSorter] = useState<SorterResult<AdminLogEntryWithUid>>({});
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState<AdminLogEntryWithUid[]>([]);
  const [open, setOpen] = useState(false);
  const [filterData, setFilterData] = useState(getDefault48HoursFilter);

  const columns: ColumnProps<AdminLogEntryWithUid>[] = [
    {
      title: t("Log time"),
      dataIndex: "logTime",
      defaultSortOrder: "descend",
      width: 160,
      sorter: true,
      sortDirections: ["ascend", "descend"],
      fixed: true,
    },
    {
      title: t("Log-type"),
      dataIndex: "logType",
      width: 200,
      sorter: true,
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 80,
      sorter: false,
    },
    {
      title: t("Actor"),
      dataIndex: "actor",
      sorter: true,
      width: 200,
    },
    {
      title: "IP",
      width: 150,
      dataIndex: "ip",
      sorter: true,
    },
    {
      title: t("Target"),
      width: 200,
      dataIndex: "target",
      sorter: false,
    },
    {
      title: "Parameter",
      dataIndex: "parameter",
      ellipsis: { showTitle: false },
      render: (v) => (
        <Tooltip title={v} placement="left">
          {v}
        </Tooltip>
      ),
      sorter: false,
    },
    {
      title: t("Message"),
      dataIndex: "message",
      ellipsis: { showTitle: false },
      render: (v) => (
        <Tooltip title={v} placement="left">
          {v}
        </Tooltip>
      ),
      sorter: false,
    },
  ];

  const fetchTypes = async () => {
    try {
      const response = await API.getData(API_URL.ADMIN.getAdminLogTypes());
      setLogTypes(response);
    } catch (error) {
      throw error;
    }
  };

  const fetchLogs = async (filterValues: ADMIN_getFilteredAdminLogs = {}, resetPagination?: boolean) => {
    try {
      setLoading(true);
      setFilterData(filterValues);
      const params: ADMIN_getFilteredAdminLogs = {
        ...(filterValues || filterData),
        limit: tablePagination.pageSize,
        offset: tablePagination.current > 1 ? (tablePagination.current - 1) * tablePagination.pageSize : 0,
        sortBy: tableSorter.field ? sortByMap[tableSorter.field as keyof API_AdminLogEntry] : undefined,
        sortOrder: tableSorter.order ? (tableSorter.order === "ascend" ? "ASC" : "DESC") : undefined,
      };
      const response = await API.getData<API_AdminLogResponse>(API_URL.ADMIN.getFilteredAdminLogs(), params);
      const dataWithUids = response.logs.map((i) => ({ ...i, uid: nanoid(5) }));
      setTablePagination({
        ...tablePagination,
        total: response.total,
        current: resetPagination ? 1 : tablePagination.current,
      });
      setTableData(dataWithUids);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange: TableProps<AdminLogEntryWithUid>["onChange"] = (pagination, filters, sorter) => {
    setTablePagination(pagination);
    setTableSorter((sorter as SorterResult<AdminLogEntryWithUid>) || {});
  };

  const showDrawer = () => {
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
  };

  const handleSubmitFilter = (values: ADMIN_getFilteredAdminLogs) => {
    return fetchLogs(values, true);
  };

  useEffect(() => {
    fetchTypes();
  }, []);

  useEffect(() => {
    fetchLogs(filterData);
  }, [tablePagination.current, tablePagination.pageSize, tableSorter.order, tableSorter.field]);

  return (
    <>
      <Affix offsetBottom={20} style={{ position: "fixed", bottom: 10, right: 10 }}>
        <Button
          type="primary"
          onClick={showDrawer}
          shape="circle"
          icon={<FontAwesomeIcon fixedWidth icon={["far", "filter"]} />}
        ></Button>
      </Affix>
      <div className="g-card">
        <Table<AdminLogEntryWithUid>
          name="adminLogs"
          rowKey="uid"
          dataSource={tableData}
          loading={{
            spinning: loading,
            tip: t("Loading logs..."),
          }}
          columns={columns}
          pagination={tablePagination}
          onChange={handleTableChange}
          scroll={{ x: 1500 }}
        />
      </div>
      <FilterDrawer
        isOpen={open}
        onSubmit={handleSubmitFilter}
        onClose={onClose}
        logTypes={logTypes}
        isLoading={loading}
        onResetFilter={() => setFilterData({})}
        initialValues={getDefault48HoursFilter()}
      />
    </>
  );
};
